import { z } from "zod";

import {
  ctaBannerSectionSchema,
  heroSectionSchema,
  listSectionSchema,
  pageSeoSchema,
  profileSectionSchema,
  valuesSectionSchema,
} from "@/content/schemas";
import { siteConfig } from "@/content/site";
import { validateContent } from "@/content/validateContent";

const aboutPageSchema = z.object({
  seo: pageSeoSchema,
  hero: heroSectionSchema,
  storySection: z.object({
    title: z.string().min(1),
    paragraphs: z.array(z.string().min(1)).min(1),
    highlight: z.string().optional(),
  }),
  audienceSection: listSectionSchema,
  instructorSection: profileSectionSchema,
  valuesSection: valuesSectionSchema,
  finalCta: ctaBannerSectionSchema,
});

export const aboutPageContent = validateContent("aboutPage", aboutPageSchema, {
  seo: {
    title: "About | Tech Savvy Starts Here",
    description: "Learn the story, mission, and values behind Tech Savvy Starts Here.",
    canonicalPath: "/about",
    socialImage: "/social-card.svg",
  },
  hero: {
    eyebrow: "About",
    title: "Technology Should Feel",
    accent: "Clear and Useful",
    description:
      "Tech Savvy Starts Here helps beginners, professionals, creators, parents, and small business owners use AI and digital tools in practical ways.",
    primaryCta: {
      label: "Subscribe",
      href: "/newsletter",
      external: false,
    },
    secondaryCta: {
      label: "Join the Workshop",
      href: "/workshops",
      external: false,
    },
  },
  storySection: {
    title: "A practical hub for real people using modern tools",
    paragraphs: [
      "Tech Savvy Starts Here was created to help everyday people build digital confidence without getting lost in jargon, hype, or overly technical explanations.",
      "We focus on practical, real-world ways to use AI and digital tools at work, in business, and at home, with an emphasis on clarity, usefulness, and steady growth.",
    ],
    highlight: "Start where you are. Learn what matters. Use technology with more confidence.",
  },
  audienceSection: {
    title: "Who This Is For",
    description: "Designed for people who want useful guidance they can actually apply.",
    items: [
      {
        title: "Beginners",
        description: "People who want a clear starting point for AI and digital tools.",
        icon: "Sparkles",
      },
      {
        title: "Professionals",
        description: "People who want smarter workflows and more confidence using modern tools at work.",
        icon: "Briefcase",
      },
      {
        title: "Creators and small business owners",
        description: "People building content, offers, and an online presence with better systems.",
        icon: "Layout",
      },
      {
        title: "Parents and everyday learners",
        description: "People who want to navigate technology more thoughtfully at home and in life.",
        icon: "Heart",
      },
    ],
  },
  instructorSection: {
    title: "Meet Dr. Ijeoma Ndu",
    name: "Dr. Ijeoma Ndu",
    role: "ML Engineer and AI Educator",
    description:
      "Dr. Ijeoma Ndu teaches practical AI and digital skills with a clear, beginner friendly approach. Her work focuses on helping people use modern tools with more clarity, confidence, and purpose.",
    icon: "User",
    imageSrc: siteConfig.headshotImage,
    imageAlt: "Dr. Ijeoma Ndu headshot",
  },
  valuesSection: {
    title: "Mission and Values",
    description: "Built around trust, clarity and practical learning.",
    values: [
      {
        title: "Practical first",
        description: "Everything should help people do something useful, not just understand a concept abstractly.",
        icon: "CheckSquare",
      },
      {
        title: "Beginner friendly",
        description: "Clear language matters. People should feel supported, not overwhelmed.",
        icon: "Users",
      },
      {
        title: "Modern but grounded",
        description: "Use current tools, but stay focused on what is actually helpful in real life.",
        icon: "Target",
      },
    ],
  },
  finalCta: {
    title: "Start learning with clarity and confidence",
    description: "Explore the newsletter, watch the videos, and join the live workshop to build practical digital skills.",
    primaryCta: {
      label: "Subscribe",
      href: "/newsletter",
      external: false,
    },
    secondaryCta: {
      label: "Explore the Workshop",
      href: "/workshops",
      external: false,
    },
    tone: "light",
  },
});
